import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import { RouteTransitionLogo } from '../../components/RouteTransitionLogo';
import { useBackTarget } from '../../hooks/useBackTarget';
import { useVenue } from '../../contexts/VenueContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const FilterSvg = () => (
  <svg width="29" height="24" viewBox="0 0 29 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color: 'var(--text-2)' }}>
    <path d="M19.889 2.50545H22.1542V1.14483C22.1542 0.827736 22.2822 0.542009 22.4889 0.334712C22.6974 0.127414 22.9825 8.2016e-05 23.2987 8.2016e-05C23.6152 8.2016e-05 23.9018 0.127414 24.1079 0.334712C24.3152 0.542009 24.4437 0.827736 24.4437 1.14483V2.50545H26.7071C27.3853 2.50545 27.9404 3.06029 27.9404 3.73847C27.9404 4.41695 27.3853 4.97149 26.7071 4.97149H24.4437V10.2274C24.4437 10.5432 24.3152 10.8299 24.1079 11.0359C23.9018 11.2426 23.6152 11.3718 23.2987 11.3718C22.9828 11.3718 22.6974 11.2426 22.4889 11.0359C22.2822 10.8299 22.1542 10.5432 22.1542 10.2274V4.97149H19.889C19.2109 4.97149 18.6563 4.41664 18.6563 3.73847C18.6563 3.06029 19.2109 2.50545 19.889 2.50545ZM10.5194 12.8029H12.7837V1.14483C12.7837 0.827736 12.9119 0.542009 13.1192 0.334712C13.3256 0.127414 13.6123 8.2016e-05 13.9291 8.2016e-05C14.2434 8.2016e-05 14.5307 0.127414 14.7373 0.334712C14.9446 0.542009 15.072 0.827736 15.072 1.14483V12.8026H17.3372C18.0153 12.8026 18.5705 13.3575 18.5705 14.0356C18.5705 14.7138 18.0153 15.269 17.3372 15.269H15.072V22.1939C15.072 22.5097 14.9446 22.7964 14.7373 23.0025C14.5307 23.2091 14.2437 23.3383 13.9291 23.3383C13.612 23.3383 13.3256 23.2091 13.1192 23.0025C12.9119 22.7964 12.7837 22.5097 12.7837 22.1939V15.2693H10.5194C9.84123 15.2693 9.28639 14.7141 9.28639 14.0359C9.28608 13.3575 9.84093 12.8029 10.5194 12.8029ZM1.23284 8.38796H3.44545V1.14483C3.44545 0.827736 3.57432 0.542009 3.78192 0.334712C3.9883 0.127414 4.27495 8.2016e-05 4.59081 8.2016e-05C4.90637 8.2016e-05 5.19272 0.127414 5.40002 0.334712C5.60731 0.542009 5.73464 0.827736 5.73464 1.14483V8.38796H8.0506C8.72877 8.38796 9.28362 8.9428 9.28362 9.62098C9.28362 10.2992 8.72877 10.854 8.0506 10.854H5.73464V22.1942C5.73464 22.51 5.60701 22.7967 5.40002 23.0028C5.19272 23.2094 4.90637 23.3386 4.59081 23.3386C4.27464 23.3386 3.9883 23.2094 3.78192 23.0028C3.57463 22.7967 3.44545 22.51 3.44545 22.1942V10.854H1.23284C0.554658 10.854 -0.000188344 10.2992 -0.000188344 9.62098C-0.000188344 8.94249 0.554658 8.38796 1.23284 8.38796Z" fill="currentColor"/>
    <path d="M23 12.0002L24.4164 16.5838H29L25.2918 19.4167L26.7082 24.0002L23 21.1674L19.2918 24.0002L20.7082 19.4167L17 16.5838H21.5836L23 12.0002Z" fill="currentColor"/>
  </svg>
);

function Avatar({ name, photoUrl }) {
  const colors = ['#E8D5B7', '#D5C4A1', '#F0D9B5', '#C7B299', '#B8C4D8', '#D4B8D0'];
  const safeName = (name || 'A').trim() || 'A';
  const idx = safeName.charCodeAt(0) % colors.length;
  if (photoUrl) {
    return (
      <div style={{ width: 44, height: 44, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: '2px solid var(--surface)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <img src={photoUrl} alt={safeName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }
  return (
    <div style={{ width: 44, height: 44, borderRadius: '50%', backgroundColor: colors[idx], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '2px solid var(--surface)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
      <span style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: 'var(--text-2)' }}>{safeName.charAt(0).toUpperCase()}</span>
    </div>
  );
}

const cardStyle = {
  backgroundColor: 'var(--app-bg)',
  borderRadius: 20,
  padding: '14px 16px',
  marginBottom: 12,
  border: '1px solid var(--card-border)',
  boxShadow: 'var(--premium-shadow-main)',
};

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
};

export default function VenueReviewsPage() {
  const goBack = useBackTarget();
  const { activeVenue } = useVenue();
  const venueId = activeVenue?.live_venue_id || activeVenue?.venue_id || '';

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [sortOrder, setSortOrder] = useState('high');

  const token = useMemo(() => (typeof window !== 'undefined' ? window.localStorage.getItem('token') : ''), []);

  const fetchReviews = useCallback(async () => {
    if (!venueId || !token) {
      setReviews([]);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/venue-reviews/venue-admin/${venueId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReviews(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (err) {
      console.error('Failed to load venue reviews', err);
      toast.error('Could not load reviews');
    } finally {
      setLoading(false);
    }
  }, [token, venueId]);

  useEffect(() => {
    setLoading(true);
    fetchReviews();
  }, [fetchReviews]);

  const sorted = useMemo(() => {
    const list = [...reviews];
    list.sort((a, b) => (sortOrder === 'high' ? b.rating - a.rating : a.rating - b.rating));
    return list;
  }, [reviews, sortOrder]);

  const avgRating = useMemo(() => {
    if (!reviews.length) return '0.0';
    const sum = reviews.reduce((acc, r) => acc + Number(r.rating || 0), 0);
    return (sum / reviews.length).toFixed(1);
  }, [reviews]);

  const toggleSort = () => setSortOrder((prev) => (prev === 'high' ? 'low' : 'high'));

  const handleReply = async (reviewId) => {
    const text = replyText.trim();
    if (!text || submittingReply) return;
    setSubmittingReply(true);
    try {
      const res = await axios.post(
        `${API}/venue-reviews/${reviewId}/reply`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updatedReply = res.data?.review?.venue_reply || { text, created_at: new Date().toISOString() };
      setReviews((prev) => prev.map((r) => (r.id === reviewId ? { ...r, venue_reply: updatedReply } : r)));
      setReplyingTo(null);
      setReplyText('');
      toast.success('Reply posted');
    } catch (err) {
      console.error('Failed to post reply', err);
      toast.error(err?.response?.data?.detail || 'Could not post reply');
    } finally {
      setSubmittingReply(false);
    }
  };

  return (
    <div className="app-shell" style={{ minHeight: '100vh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 40 }}>
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-start', padding: '16px 20px 0' }}>
        <button onClick={goBack} data-testid="venue-reviews-back-btn" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <ChevronLeft size={30} color="var(--text)" strokeWidth={2} />
        </button>
      </div>
      <div style={{ marginTop: 8 }}>
        <RouteTransitionLogo width={100} height={89} dataTestId="venue-reviews-shared-logo" />
      </div>
      <h2 style={{ fontFamily: font, fontSize: 26, fontWeight: 700, color: 'var(--text)', marginTop: 8 }} data-testid="venue-reviews-heading">
        Ratings & Reviews
      </h2>
      <p style={{ fontFamily: font, fontSize: 28, fontWeight: 700, color: 'var(--text)', margin: '0 0 2px' }} data-testid="venue-reviews-average">
        {avgRating} <span style={{ fontSize: 26 }}>⭐</span>
      </p>

      <div style={{ width: '100%', maxWidth: 360, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', marginBottom: 16 }}>
        <p style={{ fontFamily: font, fontSize: 18, color: 'var(--text-2)', margin: 0 }} data-testid="venue-reviews-count">
          {reviews.length} {reviews.length === 1 ? 'Review' : 'Reviews'}
        </p>
        <button
          onClick={toggleSort}
          data-testid="review-filter-btn"
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
          title={sortOrder === 'high' ? 'Showing highest first' : 'Showing lowest first'}
        >
          <FilterSvg />
        </button>
      </div>

      <div style={{ width: '100%', maxWidth: 360, padding: '0 16px' }}>
        {loading ? (
          <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-3)', textAlign: 'center', marginTop: 40 }} data-testid="venue-reviews-loading">
            Loading reviews…
          </p>
        ) : sorted.length === 0 ? (
          <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-3)', textAlign: 'center', marginTop: 40 }} data-testid="venue-reviews-empty">
            No reviews yet. They'll appear here once guests rate you.
          </p>
        ) : (
          sorted.map((r) => (
            <div key={r.id} style={cardStyle} data-testid={`venue-review-card-${r.id}`}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Avatar name={r.guest_name} photoUrl={r.guest_photo_url} />
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)' }} data-testid={`venue-review-name-${r.id}`}>
                      {r.guest_name || 'An Allure guest'}
                    </span>
                    {r.created_at ? (
                      <span style={{ fontFamily: font, fontSize: 12, color: 'var(--text-3)' }}>{formatDate(r.created_at)}</span>
                    ) : null}
                  </div>
                </div>
                <span style={{ fontFamily: font, fontSize: 18, fontWeight: 700, color: 'var(--text)' }} data-testid={`venue-review-rating-${r.id}`}>
                  {Number(r.rating || 0).toFixed(1)} <span style={{ fontSize: 18 }}>⭐</span>
                </span>
              </div>
              {r.review_text ? (
                <p style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', margin: '0 0 10px', lineHeight: 1.4, whiteSpace: 'pre-line' }} data-testid={`venue-review-text-${r.id}`}>
                  {r.review_text}
                </p>
              ) : null}
              {r.venue_reply?.text ? (
                <div style={{ backgroundColor: 'var(--surface-2)', borderRadius: 10, padding: '8px 12px', marginBottom: 8, borderLeft: '3px solid var(--accent-purple)' }}>
                  <p style={{ fontFamily: font, fontSize: 13, fontWeight: 700, color: 'var(--accent-purple)', margin: '0 0 2px' }}>Owner Reply</p>
                  <p style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', margin: 0 }} data-testid={`venue-review-reply-${r.id}`}>
                    {r.venue_reply.text}
                  </p>
                </div>
              ) : null}
              {replyingTo === r.id ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write a reply..."
                    data-testid={`venue-review-reply-input-${r.id}`}
                    style={{ flex: 1, fontFamily: font, fontSize: 14, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--card-border)', outline: 'none', background: 'var(--surface)', color: 'var(--text)' }}
                  />
                  <button
                    onClick={() => handleReply(r.id)}
                    disabled={submittingReply || !replyText.trim()}
                    data-testid={`venue-review-reply-send-${r.id}`}
                    style={{
                      fontFamily: font, fontSize: 14, fontWeight: 700, color: 'var(--accent-purple)',
                      background: 'none', border: '1px solid var(--card-border)', borderRadius: 8,
                      padding: '4px 12px', cursor: submittingReply ? 'not-allowed' : 'pointer',
                      opacity: submittingReply || !replyText.trim() ? 0.5 : 1,
                    }}
                  >
                    {submittingReply ? 'Sending…' : 'Send'}
                  </button>
                </div>
              ) : (
                !r.venue_reply?.text && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      onClick={() => { setReplyingTo(r.id); setReplyText(''); }}
                      data-testid={`venue-review-reply-btn-${r.id}`}
                      style={{ fontFamily: font, fontSize: 16, color: 'var(--text-2)', background: 'none', border: '1px solid var(--card-border)', borderRadius: 20, padding: '5px 28px', cursor: 'pointer' }}
                    >
                      Reply
                    </button>
                  </div>
                )
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
